<script setup>
import { computed } from 'vue';
import { achievementRanges, resolveAchievementLevel } from './data/reportLogic';

const props = defineProps({
  schulType: { type: String, default: 'E' }, // 'E' | 'M' | 'H'
  achievementLevel: { type: Number, default: 1 }, // 1-4
  totalScore: { type: Number, default: 0 }, // 총점
  scoreRange: { type: Object, default: null }, // 미사용(호환용)
  studentData: { type: Object, default: null } // 전체 학생 데이터
});

const schulType = computed(() => props.studentData?.schul_type || props.schulType || 'E');

const data = computed(() => {
  const studentData = props.studentData;
  const score = studentData ? Number(studentData.total_score) : Number(props.totalScore);
  // 결과지에 표시하는 점수는 원점수 그대로 쓴다(척도점수 변환 안 함)
  return {
    studentName: studentData?.name || '학생',
    grade: studentData?.grade_code || '',
    studentScore: Number.isFinite(score) ? score : 0,
    level: resolveAchievementLevel(schulType.value, studentData?.achievement_level ?? props.achievementLevel, score)
  };
});

// 학교급별 원점수 구간 (ACHIEVEMENT_CUTS가 없으면 구간 텍스트 없이 수준만 표시)
const levels = computed(() => {
  const ranges = achievementRanges(schulType.value);
  const base = ranges
    ? [...ranges].sort((a, b) => a.level - b.level)
    : [1, 2, 3, 4].map(level => ({ level, min: null, max: null }));
  return base.map(item => ({
    level: item.level,
    rangeMin: item.min,
    rangeMax: item.max,
    hasRange: item.min != null && item.max != null,
    isStudent: item.level === data.value.level,
    isAverage: false
  }));
});

const currentStudentLevel = computed(() => {
  return levels.value.find(l => l.isStudent) || levels.value[0];
});
</script>
<template>
  <div class="card">
    <h4 class="title-box">AI·디지털 리터러시 성취 수준과 점수</h4>
    <div class="level-chart" v-if="levels && levels.length > 0">         
      <ul :class="{ 'level-one': currentStudentLevel.level === 1 }">
        <!-- PDF 호환 캐릭터 이미지 -->
        <div 
          class="character-bg" 
          :class="{ 'char-level-one': currentStudentLevel.level === 1 }"
        ></div>
        <li v-for="item in levels" :key="item.level" :class="{ 'is-active': item.isStudent }">
          <div v-if="item.isStudent" class="my-info">
            <div class="my-level">
              <p>나의 수준</p>
              <p class="val">{{ item.level }}</p>
            </div>
            <div class="my-score">
              <p>나의 점수</p>
              <p class="val">{{ data?.studentScore || 0 }}</p>
            </div>                  
          </div>
          <div class="level-wrap"> 
            <p class="lvl-text">{{ item.level }}수준</p>             
            <p class="range-text" v-if="item.hasRange">{{ item.rangeMin }} ~ {{ item.rangeMax }}</p>     
          </div>     
        </li>
      </ul>        
    </div>  
    <div class="student-info text-base flex items-start pt-2" v-if="data">
      <img src="@/assets/images/result/ai/icon_checked.png" class="mr-2">
      <p><span class="ai-highlight">{{ data.studentName || '학생' }}</span> 학생의 AI·디지털 리터러시 점수는 <span class="ai-highlight">{{ data.studentScore || 0 }}</span>점으로 성취 수준은 <span class="ai-highlight">{{ data.level }}수준</span>에 해당합니다.</p>      
    </div>   
  </div>  
</template>

<style lang="scss" scoped>
@use "sass:list";
$ai-levels: (
  var(--result-ai-primary-60),
  var(--result-ai-primary-70),
  var(--result-ai-primary-80),
  var(--result-ai-primary-90)
);
.level-chart {
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border: 1px solid var(--result-ai-primary-500);
  border-radius: 10px;
  margin-top: 10px;
  ul {
    display: flex;    
    width: 880px;
    padding-top: 125px;
    position: relative;
    li {
      width: 25%;
      text-align: center;   
      position: relative;
      @for $i from 1 through 4 {
        &:nth-of-type(#{$i}) {
          .level-wrap {
            background-color: list.nth($ai-levels, $i);
          }
        }
      }
      .level-wrap {              
        padding: .625rem 1rem;
        position: relative;
        font-size: 20px;       
         border-right: .5rem solid var(--smoe-white);
        P:nth-child(1) { font-family: var(--smoe-font-visual); padding-bottom: 5px; font-weight: 600; margin-bottom: .5rem;  border-bottom: 2px solid var(--smoe-white); line-height: 1.7; }
        P:nth-child(2) {
          background-color: var(--smoe-white);
          border-radius: 50px;  
          font-weight: 500;
          padding: 2px 0;
        }
        &::before {
          content: '';
          position: absolute;
          left: -.28rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1px;
          height: 130px;
          background-image: url("@/assets/images/result/ai/line_vertical.png");
        }
      }
      .my-info {
        position: absolute;
        top: -125px;
        left: 50%;
        transform: translateX(-50%);
        width: 90%;    
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 8px 10px 10px 10px;
        border-radius: 10px;
        color: var(--smoe-white);
        font-size: 18px;
        background: linear-gradient(180deg, var(--result-ai-primary-600) 0%, var(--result-ai-primary-100) 100%);
        .my-level, .my-score {width: calc((100% - 10px) / 2);}
        p:nth-child(1) {     
          font-family: var(--smoe-font-visual);
          border-bottom: 2px solid var(--smoe-white);
          line-height: 1.7;
          margin-bottom: .5rem;
        }
        p:nth-child(2) {
          background-color: var(--smoe-white);
          color: var(--smoe-black);
          border-radius: 1.625rem;         
          font-weight: 600;
          padding: 4px 0;        
        }
        &::before, &::after {
          content:'';
          position: absolute;    
        }
        &::before {
          bottom: 10px;
          left: -55px;
          width: 73px;
          height: 73px;
          background-image: url("@/assets/images/result/ai/char_02.png");
        }
        &::after {                
          left: 50%;
          bottom: -18px;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-style: solid;         
          border-width: 14px 10px 0px 10px;
          border-color: var(--result-ai-primary-100) transparent transparent transparent;  
        }        
      }   
      &:nth-of-type(1) {
        .level-wrap {
          border-top-left-radius: 1.125rem ;
          border-bottom-left-radius: 1.125rem ;        
          &::before {
            display: none;
          }     
        }
      }      
      &:nth-of-type(4) {
        .level-wrap {
          border-top-right-radius: 1.125rem ;
          border-bottom-right-radius: 1.125rem ;
          border-right:0;    
        }
      }    
    }
    
    // PDF 호환 캐릭터 이미지
    .character-bg {
      position: absolute;
      top: -4px; // ul 기준으로 원래 위치
      left: 0;
      width: 130px;
      height: 130px;
      background: url("@/assets/images/result/ai/char_03.png") no-repeat;
      background-size: 130px 130px;
      pointer-events: none;
      z-index: 1;
      
      &.char-level-one {
        right: 0;
        left: auto;
      }
    }
    
    // 기존 가상 요소 숨김 (브라우저용)
    &::before {
      display: none !important;
    }
    &.level-one::before {
      display: none !important;
    }
  } 
}
</style>